<template>
  <v-container>
    <v-dialog v-model="dialog" max-width="500px">
      <v-form v-model="valid" ref="formData">
        <v-card class="pt-3">
          <v-card-title>
            <span class="headline ml-2"
              >{{ isEdit ? $t("edit") : $t("add") }} {{ $t("allowance") }}</span
            >
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-autocomplete
                    v-model="dataItem.positionId"
                    :items="positionList"
                    hide-no-data
                    hide-selected
                    item-text="name"
                    item-value="id"
                    :label="$t('position')"
                    required
                    :rules="[...rules2, validator.alfaNumberNotRequired]"
                    class="sae-required"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="12" md="12">
                  <v-autocomplete
                    v-model="dataItem.componentId"
                    :items="componentList"
                    hide-no-data
                    hide-selected
                    item-text="name"
                    item-value="id"
                    :label="$t('component')"
                    required
                    :rules="[...rules2, validator.alfaNumberNotRequired]"
                    class="sae-required"
                    return-object
                    @change="changeAmounValue"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field
                    v-if="dialog && !loading"
                    type="text"
                    :label="$t('amount')"
                    v-model="dataItem.amount"
                    v-mask="mask"
                    :rules="[...rules3, validator.numberNotRequired]"
                    required
                    class="sae-required"
                  ></v-text-field>
                  <v-text-field
                    v-show="dialog && loading"
                    type="text"
                    :label="$t('amount')"
                    v-mask="mask"
                    required
                    class="sae-required"
                  ></v-text-field>
                </v-col>
                <v-col md="6">
                  <v-menu
                    ref="menu"
                    v-model="menuStart"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="dateFormatted(dataItem.startDate)"
                        :label="$t('effective_date')"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-on="on"
                        color="primary"
                        :rules="rules"
                        required
                        class="sae-required"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="dataItem.startDate"
                      @change="onStartDateChange"
                      no-title
                      scrollable
                      :locale="$t('locale')"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col md="6" class="pb-0">
                  <v-menu
                    ref="menu"
                    v-model="menuEnd"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="dateFormatted(dataItem.endDate)"
                        :label="$t('end_date')"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-on="on"
                        color="primary"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="dataItem.endDate"
                      @change="onEndDateChange"
                      no-title
                      scrollable
                      :locale="$t('locale')"
                      :min="dataItem.startDate"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions class="mx-6 pb-6">
            <v-row justify="center">
              <v-btn color="primary" text @click="close">{{
                $t("cancel")
              }}</v-btn>
              <v-btn
                :loading="loading"
                color="primary"
                @click="saveOrUpdate(dataItem)"
                :disabled="!valid"
                >{{ $t("save") }}</v-btn
              >
            </v-row>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <v-dialog v-model="deleteDialog" persistent max-width="320">
      <v-card>
        <v-card-title class="headline">{{
          $t("delete_allowance")
        }}</v-card-title>
        <v-card-text>{{ $t("you_sure") }}</v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="deleteDialog = false">{{
            $t("cancel")
          }}</v-btn>
          <v-btn
            color="error"
            :loading="loading"
            @click="deleteComponent(dataItem)"
            >{{ $t("delete") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row>
      <v-col md="9">
        <v-text-field
          :rules="[validator.alfaNumberNotRequired]"
          :label="$t('search')"
          prepend-inner-icon="mdi-magnify"
          single-line
          v-model="search"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-btn
          right
          color="primary"
          outlined
          dark
          class="mb-2"
          @click="addItem()"
          >{{ $t("add_allowance") }}</v-btn
        >
      </v-col>
    </v-row>

    <v-data-table
      :loading="loading"
      :search="search"
      :items="dataList"
      :no-data-text="$t('no_data')"
      :headers="headers"
      :footer-props="{
        'items-per-page-options': [10, 50, 100],
        'items-per-page-text': $t('rows_per_page'),
      }"
      class="elevation-1 mt-3"
    >
      <template v-slot:footer.page-text="items">
        {{ items.pageStart }} - {{ items.pageStop }} {{ $t("of") }}
        {{ items.itemsLength }}
      </template>
      <template v-slot:item.no="{ item }">{{
        dataList
          .map(function(x) {
            return x.id;
          })
          .indexOf(item.id) + 1
      }}</template>
      <template v-slot:item.status="{ item }">
        <div v-if="item.status">
          <v-icon small class="ml-3" color="success"
            >mdi-check-circle-outline</v-icon
          >
        </div>
        <div v-else>
          <v-icon small class="ml-3" color="error"
            >mdi-alert-circle-outline</v-icon
          >
        </div>
      </template>
      <template v-slot:item.action="{ item }">
        <span v-if="item.schedule_code != 'x' && item.schedule_code != 'X'">
          <v-icon small class="mr-2" color="primary" @click="editedItem(item)"
            >mdi-pencil</v-icon
          >
          <v-icon small color="error" @click="deleteItem(item)"
            >mdi-delete</v-icon
          >
        </span>
      </template>
      <template v-slot:item.amount="{ item }">
        {{item.amount ? item.amount.toLocaleString('en-US', { maximumFractionDigits:0 }) : "-"}}
      </template>
      <template v-slot:item.component.name="{ item }"
        >{{ item.component.name }} ({{ item.component.code }})</template
      >
    </v-data-table>
  </v-container>
</template>

<script>
import moment from "moment";

export default {
  name: "PositionAllowance",
  data() {
    return {
      mask: null,
      isEdit: false,
      deleteScheduleTime: false,
      dialog: false,
      deleteDialog: false,
      dataList: [],
      positionList: [],
      componentList: [],
      headers: [
        { text: "No", value: "no" },
        { text: "Salary Component", value: "component.name" },
        { text: this.$i18n.t("position"), value: "position.name" },
        { text: this.$i18n.t("value"), value: "amount" },
        { text: this.$i18n.t("start_date"), value: "startDate" },
        { text: this.$i18n.t("end_date"), value: "endDate" },
        { text: this.$i18n.t("action"), value: "action" },
      ],
      company_id: this.$store.state.company,
      validator: this.$store.state.validator,
      baseUrl: this.$store.state.baseUrl,
      dataItem: {
        id: 0,
        componentId: null,
        positionId: null,
        amount: null,
        startDate: moment()
          .locale(this.$i18n.t("lang"))
          .format("YYYY-MM-DD"),
        endDate: null,
      },
      search: "",
      editedIndex: -1,
      valid: true,
      rules: [(v) => !!v || this.$i18n.t("required")],
      rules2: [
        (value) =>
          value == 0
            ? true
            : value
            ? value > 0
            : !!value || this.$i18n.t("required"),
      ],
      rules3: [
        (v) => !!v || this.$i18n.t("required"),
        (v) => /^[0-9\-.,]+$/.test(v) || "Only accept numbers",
        (v) => (v || "").length <= 14 || "11 Char Max",
      ],
      loading: false,

      menuStart: null,
      menuEnd: null,
    };
  },
  methods: {
    changeAmounValue(data) {
      this.dataItem.componentId = data.id;
      this.dataItem.amount = parseInt(data.amount);
      this.loading = true;
      setTimeout(() => {
        this.loading = false;
      }, 50);
    },
    onStartDateChange() {
      this.menuStart = false;
      if (this.dataItem.endDate)
        this.dataItem.endDate = this.dataItem.startDate;
    },
    onEndDateChange() {
      this.menuEnd = false;
    },
    dateFormatted(date) {
      if (date)
        return moment(date)
          .locale(this.$i18n.t("lang"))
          .format("Do MMMM YYYY");
      return null;
    },
    costumHeader(actionId, action) {
      return { headers: { actionId, action } };
    },
    showMessage(type, msg) {
      this.$store.dispatch("nShowMessage", { type, msg });
    },
    async getPosition() {
      this.loading = true;
      const result = await this.$store.dispatch("getPosition");
      this.loading = false;
      return result;
    },
    async getComponentPayroll() {
      this.loading = true;
      const result = await this.$store.dispatch("getPayrollComponent", {
        status: 1,
        positionOnly: 1,
      });
      this.loading = false;
      return result;
    },
    async getComponent() {
      this.loading = true;
      const result = await this.$store.dispatch("getPositionAllowance");
      this.loading = false;
      return result;
    },
    async saveOrUpdate(data) {
      if (this.isEdit) {
        this.editComponent(data);
      } else {
        this.saveComponent(data);
      }
    },
    async saveComponent(data) {
      await this.beforeSave();
      this.loading = true;
      const body = { ...data, amount: parseInt(data.amount) };
      const result = await this.$store.dispatch("savePositionAllowance", body);
      if (result == true) {
        this.showMessage("success", this.$i18n.t("alert_save"));
        this.dialog = false;
        this.dataList = await this.getComponent();
      }
      this.loading = false;
      await this.afterSave();
    },
    async editComponent(data) {
      await this.beforeSave();
      const body = { ...data, amount: parseInt(data.amount) };
      this.loading = true;
      const result = await this.$store.dispatch("editPositionAllowance", body);
      if (result == true) {
        this.showMessage("success", this.$i18n.t("alert_save"));
        this.dialog = false;
        this.dataList = await this.getComponent();
      }
      this.loading = false;
      await this.afterSave();
    },
    async deleteComponent(data) {
      this.loading = true;
      const result = await this.$store.dispatch(
        "deletePositionAllowance",
        data
      );
      if (result == true) {
        this.showMessage("success", "Data Deleted");
        this.deleteDialog = false;
        this.dataList = await this.getComponent();
      }
      this.loading = false;
    },
    addItem() {
      this.isEdit = false;
      this.dialog = true;
      this.dataItem = Object.assign({ amount: null, startDate: null });
      setTimeout(() => {
        this.$refs.formData.reset();
      }, 100);
    },
    close() {
      this.dialog = false;
    },
    editedItem(item) {
      this.isEdit = true;
      this.dataItem = Object.assign({}, item);
      this.dataItem.componentId = item.component.id;
      this.dataItem.positionId = item.position.id;
      this.dialog = true;
    },
    deleteItem(item) {
      this.dataItem = Object.assign({}, item);
      this.deleteDialog = true;
    },
    async beforeSave() {
      this.mask = await this.$store.dispatch("getNumberMaskFormat", false);
    },
    async afterSave() {
      this.mask = await this.$store.dispatch("getNumberMaskFormat");
    },
  },
  async beforeMount() {
    this.mask = await this.$store.dispatch("getNumberMaskFormat");
  },
  async created() {
    this.dataList = await this.getComponent();
    this.positionList = await this.getPosition();
    this.componentList = await this.getComponentPayroll();
  },
  watch: {
    dialog(show) {
      if (show && !this.isEdit) {
        if (this.$refs.formData) {
          this.$refs.formData.reset();
          this.dataItem = {
            id: 0,
            componentId: null,
            positionId: null,
            amount: null,
            startDate: null,
            endDate: null,
          };
        }
      }
    },
  },
};
</script>
